<template>
  <!--
    MGI-PILOT-SELF-SERVICE-CREDENTIAL-ROTATION-WS1 · the signed-in user changes ITS OWN password.
    There is no identity selector and none is sent: the backend rotates the session's own credential only.
    The values live only in this component's refs while it is open, are cleared on close and after submit,
    are never persisted and never logged. On success every session of this identity has been revoked by the
    backend, so the user is signed out locally (canonical teardown) and sent to the login page.
  -->
  <v-dialog :model-value="modelValue" max-width="460" persistent @update:model-value="close">
    <v-card data-testid="change-password-dialog">
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-lock-reset" class="mr-2" />
        Cambiar contraseña
      </v-card-title>
      <v-card-text>
        <p class="text-body-2 mb-4">
          Por seguridad, al cambiarla se cerrarán todas tus sesiones abiertas y tendrás que iniciar sesión de nuevo.
        </p>
        <v-form @submit.prevent="submit">
          <v-text-field
            v-model="currentPassword"
            data-testid="current-password"
            label="Contraseña actual"
            :type="show ? 'text' : 'password'"
            autocomplete="current-password"
            density="comfortable"
            :disabled="submitting"
          />
          <v-text-field
            v-model="newPassword"
            data-testid="new-password"
            label="Nueva contraseña"
            :type="show ? 'text' : 'password'"
            autocomplete="new-password"
            :hint="`Al menos ${SELF_ROTATION_POLICY.minLength} caracteres`"
            density="comfortable"
            :disabled="submitting"
          />
          <v-text-field
            v-model="confirmPassword"
            data-testid="confirm-password"
            label="Confirmar nueva contraseña"
            :type="show ? 'text' : 'password'"
            autocomplete="new-password"
            density="comfortable"
            :disabled="submitting"
          />
          <v-checkbox v-model="show" label="Mostrar contraseñas" density="compact" hide-details :disabled="submitting" />
          <v-alert v-if="visibleFormError" data-testid="change-password-form-error" type="info" variant="tonal" density="compact" class="mt-3">
            {{ visibleFormError }}
          </v-alert>
          <v-alert v-if="message" data-testid="change-password-message" type="error" variant="tonal" density="compact" class="mt-3">
            {{ message }}
          </v-alert>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="submitting" data-testid="change-password-cancel" @click="close(false)">Cancelar</v-btn>
        <v-btn color="primary" :loading="submitting" :disabled="!!formError || submitting" data-testid="change-password-submit" @click="submit">
          Cambiar contraseña
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import NotificationService from '@/services/notification.service'
import {
  SELF_ROTATION_POLICY,
  selfRotationFormError,
  selfRotationRefusalMessage,
} from '@/services/auth/selfCredentialRotation'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void }>()

const authStore = useAuthStore()
const router = useRouter()

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const show = ref(false)
const submitting = ref(false)
const message = ref<string | null>(null)
const attempted = ref(false)

const formError = computed(() => selfRotationFormError(currentPassword.value, newPassword.value, confirmPassword.value))
// WS1-R2 · the reason the form cannot be sent is SHOWN (mismatch, same as current, too short, over 72 bytes…) as soon as
// the user has typed something or tried to submit — a disabled button alone never explains itself.
const visibleFormError = computed(() => {
  const touched = attempted.value || !!(currentPassword.value || newPassword.value || confirmPassword.value)
  return touched ? formError.value : null
})

function clearSecrets(): void {
  currentPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
  show.value = false
  attempted.value = false
}

function close(value = false): void {
  if (submitting.value) return
  clearSecrets()
  message.value = null
  emit('update:modelValue', value)
}

async function submit(): Promise<void> {
  if (submitting.value || !props.modelValue) return
  if (formError.value) { attempted.value = true; return }
  submitting.value = true
  message.value = null
  try {
    const outcome = await authStore.rotateOwnCredential(currentPassword.value, newPassword.value)
    if (outcome.ok) {
      clearSecrets()
      emit('update:modelValue', false)
      NotificationService.success({ message: 'Contraseña actualizada. Inicia sesión con tu nueva contraseña.', position: 'top-center', duration: 5000 })
      await router.push('/login')
      return
    }
    if (outcome.status === 401) {
      // The session is gone (the store has already torn it down): nothing is left to retry here.
      clearSecrets()
      emit('update:modelValue', false)
      NotificationService.warning({ message: selfRotationRefusalMessage(outcome), position: 'top-center', duration: 5000 })
      await router.push('/login')
      return
    }
    message.value = selfRotationRefusalMessage(outcome)
    currentPassword.value = ''
  } finally {
    submitting.value = false
  }
}

onBeforeUnmount(clearSecrets)
</script>
